// Procedural player ship hulls: every run seed yields a distinct but always
// readable ship — a symmetric neon outline in the warm-gold family. The same
// seed always produces the same ship, so replays render identically.

import * as THREE from 'three';

export interface ShipSpec {
  /** Closed hull outline, nose at +Y, counter-clockwise. Unit-ish space. */
  outline: { x: number; y: number }[];
  /** Cockpit dot position/radius (hull-local). */
  cockpit: { x: number; y: number; r: number };
  /** Engine block: tail height and half-width. */
  engine: { y: number; halfWidth: number };
  /** Warm-family hue (0.09–0.14 keeps the gold player read). */
  hue: number;
  /** Barrel-stud marks along the wing edges (striker trait), hull-local. */
  studs: { x: number; y: number; r: number }[];
}

/** Trait point-buy shaping (spec §3.1): 0–4 points per trait, all 0 = base. */
export interface ShipBias {
  swift: number;
  bulwark: number;
  striker: number;
}

/** Uniform hull scale vs the original hand-drawn ship (1.33× the brief 0.75). */
export const SHIP_SCALE = 1.15;

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function generateShipSpec(seed: number, bias?: ShipBias): ShipSpec {
  const rnd = mulberry32((seed ^ 0x9E3779B9) >>> 0);
  const swift = bias?.swift ?? 0;
  const bulwark = bias?.bulwark ?? 0;
  const striker = bias?.striker ?? 0;

  // FTL-inspired silhouette: a boxy freighter, not a jet — flat nose cap,
  // nearly straight sides, chunky mid-section, broad engine block.
  let noseW = 0.06 + rnd() * 0.12; // flat nose half-width
  let noseY = 0.38 + rnd() * 0.18; // blunt nose height
  const midY = 0.12 + rnd() * 0.18; // mid-section height
  let midW = 0.34 + rnd() * 0.2; // wide mid half-width
  const wingY = -0.18 - rnd() * 0.18; // pod trailing height
  let wingW = midW + 0.05 + rnd() * 0.15; // flush side pods
  const tailY = -0.42 - rnd() * 0.18; // tail height
  const tailW = 0.18 + rnd() * 0.16; // chunky engine block half-width
  const tailStyle = Math.floor(rnd() * 3); // 0 flat, 1 notch, 2 fork
  let bellyW = midW * (0.85 + rnd() * 0.15); // nearly straight sides

  // Trait shaping on top of the seeded base (spec §3.1): swift = smaller
  // with a longer nose and narrower mid/wings, bulwark = wider and chunkier,
  // striker = sleeker nose. Multiplicative, so all-zero bias is a no-op and
  // the seeded silhouette stays recognizable under any loadout.
  noseY *= 1 + 0.06 * swift - 0.03 * bulwark + 0.04 * striker;
  noseW *= 1 - 0.04 * swift;
  const widthMult = 1 - 0.05 * swift + 0.06 * bulwark;
  midW *= widthMult;
  wingW *= widthMult;
  bellyW *= widthMult;
  const scaleMult = 1 - 0.04 * swift + 0.03 * bulwark;

  // Right half of the outline, nose → tail (flat nose cap starts off-axis).
  const half: { x: number; y: number }[] = [
    { x: noseW, y: noseY },
    { x: bellyW, y: midY },
    { x: wingW, y: wingY },
    { x: tailW, y: tailY },
  ];
  // Center tail shape.
  const center: { x: number; y: number }[] =
    tailStyle === 1
      ? [{ x: 0, y: tailY + 0.18 }] // notch cut upward
      : tailStyle === 2
        ? [
            { x: tailW * 0.45, y: tailY + 0.14 },
            { x: 0, y: tailY },
          ] // fork
        : [{ x: 0, y: tailY }]; // flat point

  const outline = [
    ...half,
    ...center,
    // Mirror the WHOLE right half (nose corner included) up the left side:
    // the flat nose cap spans noseW → −noseW.
    ...half
      .slice()
      .reverse()
      .map((p) => ({ x: -p.x, y: p.y })),
  ];

  const cockpit = { x: 0, y: 0.1 + rnd() * 0.2, r: 0.07 + rnd() * 0.05 };
  const engine = { y: tailY, halfWidth: Math.max(0.08, tailW * 0.8) };
  const hue = 0.09 + rnd() * 0.05;

  // Striker 2+: two barrel-stud marks 70% along the belly→wing edge,
  // mirrored left/right. Derived from the wing geometry, no extra rnd draws,
  // so the base hull is identical with or without the marks.
  const studs: { x: number; y: number; r: number }[] = [];
  if (striker >= 2) {
    const sx = bellyW + (wingW - bellyW) * 0.7;
    const sy = midY + (wingY - midY) * 0.7;
    studs.push({ x: sx, y: sy, r: 0.05 }, { x: -sx, y: sy, r: 0.05 });
  }

  // Overall size bias scales the finished spec geometry uniformly.
  if (scaleMult !== 1) {
    for (const p of outline) {
      p.x *= scaleMult;
      p.y *= scaleMult;
    }
    cockpit.y *= scaleMult; // cockpit.x stays 0
    cockpit.r *= scaleMult;
    engine.y *= scaleMult;
    engine.halfWidth *= scaleMult;
    for (const s of studs) {
      s.x *= scaleMult;
      s.y *= scaleMult;
      s.r *= scaleMult;
    }
  }

  return { outline, cockpit, engine, hue, studs };
}

/** THREE.Shape for the spec, scaled to the on-screen ship size. */
export function shipShapeFromSpec(spec: ShipSpec): THREE.Shape {
  const shape = new THREE.Shape();
  const first = spec.outline[0]!;
  shape.moveTo(first.x * SHIP_SCALE, first.y * SHIP_SCALE);
  for (let i = 1; i < spec.outline.length; i++) {
    const p = spec.outline[i]!;
    shape.lineTo(p.x * SHIP_SCALE, p.y * SHIP_SCALE);
  }
  shape.closePath();
  return shape;
}
